import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Skeleton,
  Grid,
} from '@mui/material';
import {
  Close as CloseIcon,
  Public as SystemIcon,
  Business as StationIcon,
  People as PopulationIcon,
  Security as SecurityIcon,
  TrendingUp as EconomyIcon,
  Route as RouteIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { DATABASE_SERVICE_URL } from '../../services/workerUrls';
import { logService } from '../../services/logService';
import type { StarSystem, Station, NPC, JumpLane } from '../../types/gameState';

interface SystemDetailsPanelProps {
  systemId: string;
  onClose: () => void;
}

interface SystemDetails extends StarSystem {
  stations?: Station[];
  jump_lanes?: Array<JumpLane & { to_system_name?: string }>;
}

export function SystemDetailsPanel({ systemId, onClose }: SystemDetailsPanelProps) {
  const [system, setSystem] = useState<SystemDetails | null>(null);
  const [npcs, setNpcs] = useState<NPC[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchSystemDetails() {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch system details
        const systemLogId = logService.api('GET', `/api/systems/${systemId}`, {}, 'pending');
        const systemStart = Date.now();
        const systemRes = await fetch(`${DATABASE_SERVICE_URL()}/api/systems/${systemId}`);
        const systemDuration = Date.now() - systemStart;

        if (!systemRes.ok) {
          logService.updateLog(systemLogId, { status: 'error', duration: systemDuration, message: `HTTP ${systemRes.status}` });
          setError('Failed to load system details');
          setIsLoading(false);
          return;
        }

        const systemResult = await systemRes.json();
        if (!systemResult.success || !systemResult.data) {
          logService.updateLog(systemLogId, { status: 'error', duration: systemDuration, message: 'No system data' });
          setError('System not found');
          setIsLoading(false);
          return;
        }

        logService.updateLog(systemLogId, { status: 'success', duration: systemDuration });
        setSystem(systemResult.data);

        // Fetch NPCs in system
        const npcsLogId = logService.api('GET', `/api/npcs?system=${systemId}`, {}, 'pending');
        const npcsStart = Date.now();
        const npcsRes = await fetch(`${DATABASE_SERVICE_URL()}/api/npcs?system=${systemId}`);
        const npcsDuration = Date.now() - npcsStart;

        if (npcsRes.ok) {
          const npcsResult = await npcsRes.json();
          if (npcsResult.success && npcsResult.data) {
            logService.updateLog(npcsLogId, { status: 'success', duration: npcsDuration });
            setNpcs(npcsResult.data);
          } else {
            logService.updateLog(npcsLogId, { status: 'error', duration: npcsDuration, message: 'No NPCs data' });
          }
        } else {
          logService.updateLog(npcsLogId, { status: 'error', duration: npcsDuration, message: `HTTP ${npcsRes.status}` });
        }
      } catch (err) {
        setError('Failed to load system details');
      } finally {
        setIsLoading(false);
      }
    }

    fetchSystemDetails();
  }, [systemId]);

  if (isLoading) {
    return (
      <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider', height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Skeleton variant="text" width="60%" height={32} />
            <IconButton size="small" onClick={onClose}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
          <Skeleton variant="rectangular" height={200} />
        </CardContent>
      </Card>
    );
  }

  if (error || !system) {
    return (
      <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider', height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" color="error">
              Error
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {error || 'System not found'}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider', height: '100%', overflow: 'auto' }}>
      <CardContent sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SystemIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {system.name}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* System Info */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">Star Class</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500, ml: 2.5 }}>
              {system.star_class || 'Unknown'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SecurityIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">Security</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500, ml: 2.5, textTransform: 'capitalize' }}>
              {system.security_level}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EconomyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">Economy</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500, ml: 2.5, textTransform: 'capitalize' }}>
              {system.economy_type?.replace('_', ' ')}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PopulationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">Population</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500, ml: 2.5, textTransform: 'capitalize' }}>
              {system.population_class}
            </Typography>
          </Grid>
        </Grid>

        {system.description && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, backgroundColor: 'action.hover' }}>
            <Typography variant="body2" color="text.secondary">
              {system.description}
            </Typography>
          </Box>
        )}

        {/* Stations */}
        {system.stations && system.stations.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StationIcon sx={{ fontSize: 18 }} />
              Stations ({system.stations.length})
            </Typography>
            <List dense disablePadding sx={{ mb: 2 }}>
              {system.stations.map((station, index) => (
                <Box key={station.id}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ px: 1, py: 1 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {station.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            label={station.station_type?.replace('_', ' ')}
                            size="small"
                            sx={{ height: 16, fontSize: '0.6rem', textTransform: 'capitalize' }}
                          />
                          {station.has_market && (
                            <Chip
                              label="Market"
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.6rem' }}
                            />
                          )}
                          {station.has_shipyard && (
                            <Chip
                              label="Shipyard"
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.6rem' }}
                            />
                          )}
                          {station.has_repair && (
                            <Chip
                              label="Repairs"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.6rem' }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </Box>
              ))}
            </List>
          </>
        )}

        {/* Jump Lanes */}
        {system.jump_lanes && system.jump_lanes.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <RouteIcon sx={{ fontSize: 18 }} />
              Jump Lanes ({system.jump_lanes.length})
            </Typography>
            <List dense disablePadding sx={{ mb: 2 }}>
              {system.jump_lanes.map((lane, index) => (
                <Box key={lane.id}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ px: 1, py: 0.75 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          → {lane.to_system_name || lane.to_system_id}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            {lane.fuel_cost} fuel
                          </Typography>
                          {lane.jump_type !== 'standard' && (
                            <Chip
                              label={lane.jump_type}
                              size="small"
                              color={lane.jump_type === 'hazardous' ? 'error' : lane.jump_type === 'wormhole' ? 'info' : 'default'}
                              sx={{ height: 14, fontSize: '0.55rem', textTransform: 'capitalize' }}
                            />
                          )}
                          {lane.hazard_level > 3 && (
                            <Chip
                              label={`Risk ${lane.hazard_level}`}
                              size="small"
                              color="warning"
                              sx={{ height: 14, fontSize: '0.55rem' }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </Box>
              ))}
            </List>
          </>
        )}

        {/* NPCs */}
        {npcs.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PopulationIcon sx={{ fontSize: 18 }} />
              Characters in System ({npcs.length})
            </Typography>
            <List dense disablePadding>
              {npcs.slice(0, 5).map((npc, index) => (
                <Box key={npc.id}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ px: 1, py: 0.75 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {npc.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                          <Chip
                            label={npc.npc_type.replace('_', ' ')}
                            size="small"
                            color={
                              npc.npc_type === 'pirate'
                                ? 'error'
                                : npc.npc_type === 'quest_giver'
                                  ? 'warning'
                                  : 'default'
                            }
                            sx={{ height: 14, fontSize: '0.55rem', textTransform: 'capitalize' }}
                          />
                          {npc.title && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {npc.title}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </Box>
              ))}
              {npcs.length > 5 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, py: 0.5 }}>
                  +{npcs.length - 5} more...
                </Typography>
              )}
            </List>
          </>
        )}

        {/* Empty State */}
        {(!system.stations || system.stations.length === 0) &&
          (!system.jump_lanes || system.jump_lanes.length === 0) &&
          npcs.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <SystemIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                This system appears to be unexplored
              </Typography>
            </Box>
          )}
      </CardContent>
    </Card>
  );
}
